import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getPalletBalances, getPalletAuditSparkline } from '../../lib/api';
import VelocityTile from './VelocityTile';

/**
 * Balances tab — live aggregation of pallet_movements grouped by the
 * selected dimension. The "merchant_type" view doubles as the per-merchant
 * pooled-pallet ledger for CHEP/Loscam reconciliations.
 */
const VIEWS = [
  { value: 'type', label: 'By pallet type' },
  { value: 'warehouse_type', label: 'By warehouse × type' },
  { value: 'merchant_type', label: 'By merchant × type (ledger)' },
];

// Tiny inline SVG sparkline — renders a normalized line from a series of
// {balance} points. Drops a red dot on the highest downtick vs the rolling
// mean so drift stands out at a glance.
function Sparkline({ series, width = 90, height = 24 }) {
  if (!series || series.length < 2) {
    return <span className="text-[10px] text-muted-foreground font-mono">—</span>;
  }
  const values = series.map((s) => s.balance);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const step = width / (series.length - 1);
  const yFor = (v) => height - 2 - ((v - min) / range) * (height - 4);
  const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${yFor(v).toFixed(1)}`).join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  const trend = last >= first ? '#059669' : '#e11d48';
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="inline-block align-middle">
      <path d={path} fill="none" stroke={trend} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={(width - 1).toFixed(1)} cy={yFor(last).toFixed(1)} r="2" fill={trend} />
    </svg>
  );
}

export default function BalancesTab({ types }) {
  const navigate = useNavigate();
  const [view, setView] = useState('type');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sparklines, setSparklines] = useState({});  // type_id → {series}

  // Drill-through: click a sparkline → jump straight to the Loss Investigator
  // pre-filtered for that type (and warehouse/merchant when applicable).
  const drillTo = (r) => {
    const params = new URLSearchParams({ tab: 'investigator', type_id: r.type_id });
    if (r.warehouse_id) params.set('warehouse_id', r.warehouse_id);
    if (r.merchant_id) params.set('merchant_id', r.merchant_id);
    navigate(`/pallets?${params.toString()}`);
  };

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const r = await getPalletBalances({ group_by: view });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load balances');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBalances(); }, [view]);

  // Fetch a 30-day sparkline for every unique type_id present in rows.
  useEffect(() => {
    const ids = [...new Set(rows.map((r) => r.type_id).filter(Boolean))];
    const missing = ids.filter((id) => !sparklines[id]);
    if (missing.length === 0) return;
    Promise.all(missing.map((id) => getPalletAuditSparkline({ type_id: id, days: 30 })
      .then((res) => [id, res.data]).catch(() => [id, null])))
      .then((pairs) => {
        setSparklines((prev) => {
          const next = { ...prev };
          pairs.forEach(([id, data]) => { if (data) next[id] = data; });
          return next;
        });
      });
  }, [rows]);

  const grouped = useMemo(() => {
    if (view === 'type') return rows;
    // For warehouse_type and merchant_type — group by the outer key for nicer rendering.
    const outerKey = view === 'warehouse_type' ? 'warehouse_id' : 'merchant_id';
    const outerLabelKey = view === 'warehouse_type' ? 'warehouse_name' : 'merchant_name';
    const map = new Map();
    rows.forEach((r) => {
      const key = r[outerKey] || '_none';
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: r[outerLabelKey] || (r[outerKey] ? '—' : 'Untagged'),
          warehouse_id: view === 'warehouse_type' ? r.warehouse_id : null,
          merchant_id: view === 'merchant_type' ? r.merchant_id : null,
          items: [],
        });
      }
      map.get(key).items.push(r);
    });
    return [...map.values()].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [rows, view]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">Balances</h2>
          <p className="text-xs text-muted-foreground">
            Computed live from movements. Voided rows are excluded.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select className="text-sm border border-border bg-background h-9 px-2"
            value={view} onChange={(e) => setView(e.target.value)} data-testid="pallet-balance-view">
            {VIEWS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={fetchBalances} data-testid="pallet-balance-refresh">
            Refresh
          </Button>
        </div>
      </div>

      {loading && <div className="p-8 text-center text-muted-foreground">Loading…</div>}

      {!loading && view === 'type' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pallet-balance-grid-type">
          {grouped.length === 0 && (
            <div className="col-span-full p-8 text-center text-muted-foreground border border-dashed border-border">
              No movements yet. Post one to see balances appear here.
            </div>
          )}
          {grouped.map((r) => (
            <div key={r.type_id} className="border border-border bg-card p-4 flex flex-col gap-1"
              data-testid={`pallet-balance-card-${r.type_code}`}
              style={{ borderLeft: `4px solid ${r.color || '#475569'}` }}>
              <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
                <span>{r.type_code} · {r.type_name}</span>
                {r.is_pooled && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[9px]">Pooled</span>}
              </div>
              <div className={`text-3xl font-bold font-mono ${r.balance < 0 ? 'text-rose-700' : 'text-foreground'}`}>
                {r.balance.toLocaleString()}
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                <span>across {r.movements} movement{r.movements !== 1 ? 's' : ''}</span>
                <button
                  type="button"
                  title="Open in Loss Investigator"
                  data-testid={`pallet-balance-spark-${r.type_code}`}
                  onClick={() => drillTo(r)}
                  className="hover:opacity-70 cursor-pointer"
                >
                  <Sparkline series={sparklines[r.type_id]?.series} width={100} height={22} />
                </button>
              </div>
            </div>
          ))}
          </div>
          <VelocityTile />
        </>
      )}

      {!loading && view !== 'type' && (
        <div className="space-y-4" data-testid={`pallet-balance-grid-${view}`}>
          {grouped.length === 0 && (
            <div className="p-8 text-center text-muted-foreground border border-dashed border-border">
              No data yet for this view.
            </div>
          )}
          {grouped.map((g) => {
            const total = g.items.reduce((s, x) => s + x.balance, 0);
            return (
              <div key={g.key} className="border border-border bg-card">
                <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center justify-between">
                  <div className="font-semibold text-sm">{g.label}</div>
                  <div className="text-xs font-mono">Total: <strong>{total}</strong></div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/10">
                    <tr className="text-left">
                      <th className="p-2">Type</th>
                      <th className="p-2 text-right">Balance</th>
                      <th className="p-2 text-right">Movements</th>
                      <th className="p-2 text-right">30-day trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((r) => {
                      const t = types.find((x) => x.id === r.type_id);
                      return (
                        <tr key={`${g.key}-${r.type_id}`} className="border-t border-border">
                          <td className="p-2">
                            <span style={{ background: t?.color || '#475569' }} className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle" />
                            <span className="font-mono">{t?.code || r.type_id?.slice(0, 6)}</span>
                            <span className="text-muted-foreground ml-1.5 text-xs">{t?.name}</span>
                          </td>
                          <td className={`p-2 text-right font-mono font-bold ${r.balance < 0 ? 'text-rose-700' : ''}`}>
                            {r.balance}
                          </td>
                          <td className="p-2 text-right font-mono text-xs text-muted-foreground">{r.movements}</td>
                          <td className="p-2 text-right" data-testid={`pallet-balance-row-spark-${r.type_id}`}>
                            <button
                              type="button"
                              title="Open in Loss Investigator"
                              onClick={() => drillTo({ ...r, warehouse_id: g.warehouse_id, merchant_id: g.merchant_id })}
                              className="hover:opacity-70 cursor-pointer"
                            >
                              <Sparkline series={sparklines[r.type_id]?.series} width={80} height={20} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
